import { Chip, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { AppModal } from "global/components/AppModal";
import CustomTable from "global/components/CustomTable/CustomTable";
import { handleErrorMessage } from "helpers/methods";
import moment from "moment-timezone";
import { fetchIpAllowlistAudit } from "./WhitelistIPsService";
import { IpAllowlistAuditEvent } from "./WhitelistIPsTypes";
import {
  AUDIT_ACTION_LABELS,
  whitelistAuditTableHeader,
} from "./WhitelistIPsConstant";

interface CustomProps {
  isVisible: boolean;
  handleDialogClose: Function;
}

const DEFAULT_ROWS_PER_PAGE = 10;

// Green for "on"/added actions, red for "off"/removed, slate for neutral.
const actionColor = (action: string) => {
  if (
    action === "ENABLE" ||
    action === "ENTRY_ADDED" ||
    action === "ENTRY_ENABLED"
  )
    return { bg: "#ECFDF5", fg: "#047857" };
  if (
    action === "DISABLE" ||
    action === "ENTRY_REMOVED" ||
    action === "ENTRY_DISABLED"
  )
    return { bg: "#FEF2F2", fg: "#B91C1C" };
  return { bg: "#EEF2FF", fg: "#4338CA" };
};

const WhitelistIPsAuditModal = (props: CustomProps) => {
  const [events, setEvents] = useState<IpAllowlistAuditEvent[]>([]);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);

  // Server-side pagination (same shape as the contacts table): fetch only the
  // current page. The audit API has no count endpoint, so we over-fetch by one
  // row to learn whether a next page exists and size the pager accordingly.
  const fetchPage = async (currentPage: number, perPage: number) => {
    setIsLoading(true);
    try {
      const offset = (currentPage - 1) * perPage;
      const batch = await fetchIpAllowlistAudit(perPage + 1, offset);
      const rows = batch ?? [];
      const hasMore = rows.length > perPage;
      const pageRows = rows.slice(0, perPage);
      setEvents(pageRows);
      setCount(hasMore ? currentPage * perPage + 1 : offset + pageRows.length);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const rows = useMemo(
    () =>
      events.map((e, index) => {
        const colors = actionColor(e.action);
        return {
          rowId: `${e.entryId ?? "view"}-${e.createdOn ?? index}`,
          action: (
            <Chip
              size="small"
              label={AUDIT_ACTION_LABELS[e.action] ?? e.action}
              sx={{
                backgroundColor: colors.bg,
                color: colors.fg,
                fontWeight: 600,
                fontSize: "12px",
              }}
            />
          ),
          entry: (
            <Stack spacing={0.25}>
              <Typography variant="subtitle2" noWrap>
                {e.entryLabel || "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {e.entryValue || ""}
              </Typography>
            </Stack>
          ),
          change:
            e.oldValue || e.newValue ? (
              <Typography variant="caption" color="text.secondary">
                {(e.oldValue ?? "—") + " → " + (e.newValue ?? "—")}
              </Typography>
            ) : (
              "—"
            ),
          actor: (
            <Stack spacing={0.25}>
              <Typography variant="subtitle2" noWrap>
                {e.actorEmail || "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {e.actorIp || ""}
              </Typography>
            </Stack>
          ),
          when: e.createdOn ? (
            <Typography variant="caption" color="text.secondary">
              {moment(e.createdOn).format("MMM D, YYYY h:mm A")}
            </Typography>
          ) : (
            "—"
          ),
        };
      }),
    [events],
  );

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    nextPage: number,
  ) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const getBody = () => (
    <Stack spacing={1.5}>
      <CustomTable
        emptyState={{
          size: "medium",
          icon: "activity",
          accent: "violet",
          title: "No allowlist changes yet",
          body: "Every addition, edit and removal of an allowlisted IP is logged here.",
        }}
        headers={whitelistAuditTableHeader}
        rows={rows}
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleRowsPerPageChange}
        handleOnChangePageInput={handleChangePage}
        showJumpToPageNumber={false}
        showPagination={true}
        isLoading={isLoading}
      />
    </Stack>
  );

  return (
    <AppModal
      open={props.isVisible}
      onClose={() => props.handleDialogClose()}
      title="Activity Log"
      tone="neutral"
      // A paged table — the design system's L width.
      size="lg"
    >
      {getBody()}
    </AppModal>
  );
};

export default WhitelistIPsAuditModal;
